import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Client } from "../../../interfaces/client";
import {
  createClient,
  deleteClientBase,
  printClientProfile,
  sendAccountCreationConfirmation,
} from "../../../services/clientService";
import {
  uploadCreditReportForm,
  uploadSignature,
  uploadSSNTraceForm,
  uploadUserAgreement,
} from "../../../services/additionalFormService";
import { createAdditionalContact } from "../../../services/additionalContactService";
import {
  createPositionCode,
  updateServicesForPositionCode,
} from "../../../services/positionCodeService";
import { getStates, sortServices } from "../../../services/helpers";
import { DeliveryOption } from "../../../interfaces/deliveryOption";
import { getDeliveryOptions } from "../../../services/deliveryOptionsService";
import { Service } from "../../../interfaces/service";
import { getAllServices } from "../../../services/serviceService";
import { useRegistrationContext } from "../../../contexts/ClientRegistrationContext";
import { saveEmailPreferences } from "../../../services/notificationsService";

const Confirmation = () => {
  const { data } = useRegistrationContext();
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState("");
  const {
    client,
    additionalContact,
    positionCodes,
    creditReportForm,
    SSNTraceForm,
    signatureForm,
    selectedServices,
  } = data;
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [deliveryOptions, setDeliveryOptions] = useState<DeliveryOption[]>([]);
  const states = getStates();
  const [allServices, setAllServices] = useState<Service[]>([]);

  useEffect(() => {
    const initializeComponent = async () => {
      try {
        const options = await getDeliveryOptions();
        setDeliveryOptions(options);

        const allServices = await getAllServices();
        const sortedServices = await sortServices(allServices);
        const filteredServices = sortedServices.filter(
          (service) => service.clientVisible === true,
        );
        setAllServices(filteredServices);
      } catch (error) {
        console.error("Error fetching delivery options:", error);
      }
    };

    initializeComponent();
  }, []);

  // Redirect to registration if data not there
  useEffect(() => {
    if (!data.userAgreement) {
      navigate("/registration");
    }
  }, [data.userAgreement, navigate]);

  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => setErrorMessage(""), 3000);
      return () => clearTimeout(timer);
    }
  }, [errorMessage]);

  const handleSave = async () => {
    setLoading(true);
    try {
      if (client && positionCodes) {
        let createdClient: Client | null = null;

        try {
          createdClient = await createClient(client);

          // Upload files if any exist
          if (creditReportForm) {
            const response = await uploadCreditReportForm(
              creditReportForm,
              createdClient.clientID,
            );
            if (!response.success) {
              throw new Error("Failed to upload Credit Report form");
            }
          }
          if (SSNTraceForm) {
            const response = await uploadSSNTraceForm(
              SSNTraceForm,
              createdClient.clientID,
            );
            if (!response.success) {
              throw new Error("Failed to upload SSN Trace form");
            }
          }
          if (signatureForm) {
            const response = await uploadSignature(
              signatureForm,
              createdClient.clientID,
            );
            if (!response.success) {
              throw new Error("Failed to upload Signature");
            }
          }
          await uploadUserAgreement(createdClient.clientID);

          //save additional contact
          if (additionalContact?.contactEmail) {
            const newAdditionalContact = {
              ...additionalContact,
              clientID: createdClient.clientID,
            };
            await createAdditionalContact(newAdditionalContact);
          }

          // save position codes
          const positionCodesWithClientID = positionCodes.map(
            (positionCode) => ({
              ...positionCode,
              clientID: createdClient!.clientID,
            }),
          );
          for (const positionCode of positionCodesWithClientID) {
            const createdPositionCode = await createPositionCode(positionCode);
            const selectedServiceIDs = Array.from(
              selectedServices.get(positionCode.positionCodeID) || [],
            );
            await updateServicesForPositionCode(
              createdPositionCode.positionCodeID,
              selectedServiceIDs,
            );
          }

          // save email notification preferences
          await saveEmailPreferences(createdClient.clientID, [], 0);

          const pdfBlob = await printClientProfile(
            createdClient.clientID,
            false,
          );

          if (pdfBlob) {
            const url = window.URL.createObjectURL(pdfBlob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", "ClientProfile.pdf");
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setLoading(false);
          } else {
            setSuccessMessage("Failed to generate PDF.");
          }

          sendAccountCreationConfirmation(createdClient);
          handleNavigateToLogin();
          setSuccessMessage("Account created successfully.");
        } catch (error: any) {
          console.error("Error during client creation:", error);

          // Rollback if client creation failed partially
          if (createdClient) {
            try {
              await deleteClientBase(createdClient.clientID);
              console.log(
                "Rollback successful: Client and related data deleted.",
              );
            } catch (rollbackError) {
              console.error("Rollback failed:", rollbackError);
            }
          }
          navigate("/login", {
            state: {
              failMessage:
                "An error occurred during account creation. Please contact support.",
            },
          });
        }
      }
    } catch (error) {
      console.error("Unexpected error in handleSave:", error);
      setErrorMessage("An unknown error occurred. Please try again later.");
      setSuccessMessage("");
    } finally {
      setLoading(false);
    }
  };

  const handleNavigateToLogin = () => {
    const email = client?.primaryEmail;
    localStorage.setItem("email", email || "");
    localStorage.setItem("portalType", "client");

    navigate("/login", {
      state: {
        successMessage:
          "You have successfully created an account. Once your account is approved you will receive an email letting you know!",
      },
    });
  };

  const handleNavigateToKeyInformation = async () => {
    navigate("/client/registration/key-information");
    window.scrollTo(0, 0);
  };

  const handleNavigateToPositionCodes = async () => {
    navigate("/client/registration/position-codes");
    window.scrollTo(0, 0);
  };

  const renderServices = (positionCodeID: number) => (
    <>
      <div className="services-list">
        {allServices.map((service) => (
          <div key={service.serviceID} className="service-item">
            <label>
              <input
                style={{ marginRight: "10px" }}
                className={`form-check-input`}
                type="checkbox"
                checked={
                  selectedServices
                    .get(positionCodeID)
                    ?.has(service.serviceID) || false
                }
                disabled={true}
              />
              {service.serviceName}
            </label>
          </div>
        ))}
      </div>
    </>
  );

  return (
    <div className="content-container">
      <div className="header-container">
        <h1>Step 4: Confirmation</h1>
      </div>
      <div className="divider"></div>
      <div className="data-container">
        <div
          className="text-header"
          style={{ flex: "0 0 20%", textWrap: "nowrap" }}
        >
          Organization Overview
        </div>
        <div style={{ flex: "0 0 20%" }}>
          <button
            className="oxbtn btn-purple"
            onClick={handleNavigateToKeyInformation}
          >
            Edit
          </button>
        </div>
        <div className="field-stacked" style={{ flex: "0 0 60%" }}>
          <label className="field-label">Organization Name:</label>
          <div className="field-input">
            <input
              className={`form-control`}
              type="text"
              value={client?.clientName || ""}
              disabled={true}
            />
          </div>
        </div>
        <div className="field-stacked" style={{ flex: "0 0 30%" }}>
          <label className="field-label">Phone Number:</label>
          <div className="field-input">
            <input
              className={`form-control`}
              type="text"
              value={client?.phoneNumber || ""}
              disabled={true}
            />
          </div>
        </div>
        <div className="field-stacked" style={{ flex: "0 0 30%" }}>
          <label className="field-label">Confidential Fax:</label>
          <div className="field-input">
            <input
              className="form-control"
              type="text"
              value={client?.fax || ""}
              disabled={true}
            />
          </div>
        </div>
        <div className="field-stacked" style={{ flex: "0 0 60%" }}>
          <label className="field-label">Address:</label>
          <div className="field-input">
            <input
              className={`form-control`}
              type="text"
              value={client?.address || ""}
              disabled={true}
            />
          </div>
        </div>
        <div className="field-stacked" style={{ flex: "0 0 15%" }}>
          <label className="field-label">Address 2:</label>
          <div className="field-input">
            <input
              className={`form-control`}
              type="text"
              value={client?.address2 || ""}
              disabled={true}
            />
          </div>
        </div>
        <div className="field-stacked" style={{ flex: "0 0 20%" }}>
          <label className="field-label">City:</label>
          <div className="field-input">
            <input
              className={`form-control`}
              type="text"
              value={client?.city || ""}
              disabled={true}
            />
          </div>
        </div>
        <div className="field-stacked" style={{ flex: "0 0 20%" }}>
          <label className="field-label">State:</label>
          <div className="field-input">
            <select
              className={`form-select`}
              value={client?.state || ""}
              disabled={true}
            >
              <option value="">Select State</option>
              {states.map((state) => (
                <option key={state.abbreviation} value={state.abbreviation}>
                  {state.name} ({state.abbreviation})
                </option>
              ))}
            </select>
          </div>
        </div>
        {data.client?.state === "INT" && (
          <div className="field-stacked" style={{ flex: "0 0 20%" }}>
            <label className="field-label">Country:</label>
            <div className="field-input">
              <input
                className="form-control"
                type="text"
                value={data.client?.country || ""}
                disabled={true}
              />
            </div>
          </div>
        )}
        <div className="field-stacked" style={{ flex: "0 0 20%" }}>
          <label className="field-label">ZipCode:</label>
          <div className="field-input">
            <input
              className={`form-control`}
              type="text"
              value={client?.zipCode || ""}
              disabled={true}
            />
          </div>
        </div>
        <div className="field-stacked" style={{ flex: "0 0 20%" }}>
          <label className="field-label">Final Report Delivery:</label>
          <div>
            <select
              className={`form-select`}
              value={client?.deliveryOptionID || ""}
              disabled={true}
            >
              {deliveryOptions.map((option) => (
                <option
                  key={option.deliveryOptionID}
                  value={option.deliveryOptionID}
                >
                  {option.description}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Primary Contact */}
      <div className="data-container">
        <div
          className="text-header"
          style={{ flex: "0 0 20%", textWrap: "nowrap" }}
        >
          Primary Contact Information
        </div>
        <div style={{ flex: "0 0 40%" }}>
          <button
            className="oxbtn btn-purple"
            onClick={handleNavigateToKeyInformation}
          >
            Edit
          </button>
        </div>
        <div className="field-stacked" style={{ flex: "0 0 30%" }}>
          <label className="field-label">Title:</label>
          <div className="field-input">
            <input
              className={`form-control`}
              type="text"
              placeholder="Mr./Ms., The Rev., ..."
              value={client?.primaryTitle || ""}
              disabled={true}
            />
          </div>
        </div>
        <div className="field-stacked" style={{ flex: "0 0 30%" }}>
          <label className="field-label">First Name:</label>
          <div className="field-input">
            <input
              className={`form-control`}
              type="text"
              value={client?.primaryFirstName || ""}
              disabled={true}
            />
          </div>
        </div>
        <div className="field-stacked" style={{ flex: "0 0 30%" }}>
          <label className="field-label">Last Name:</label>
          <div className="field-input">
            <input
              className={`form-control`}
              type="text"
              value={client?.primaryLastName || ""}
              disabled={true}
            />
          </div>
        </div>
        <div className="field-stacked" style={{ flex: "0 0 30%" }}>
          <label className="field-label">Job Title:</label>
          <div className="field-input">
            <input
              className={`form-control`}
              type="text"
              value={client?.primaryJobTitle || ""}
              disabled={true}
            />
          </div>
        </div>
        <div className="field-stacked" style={{ flex: "0 0 60%" }}>
          <label className="field-label">Email:</label>
          <div className="field-input">
            <input
              className={`form-control`}
              type="email"
              value={client?.primaryEmail || ""}
              disabled={true}
            />
          </div>
        </div>
      </div>

      {/* Additional Contact */}
      {additionalContact?.contactEmail !== "" && (
        <div className="data-container">
          <div
            className="text-header"
            style={{ flex: "0 0 20%", textWrap: "nowrap" }}
          >
            Additional Contact Information
          </div>
          <div style={{ flex: "0 0 40%" }}>
            <button
              className="oxbtn btn-purple"
              onClick={handleNavigateToKeyInformation}
            >
              Edit
            </button>
          </div>
          <div className="field-stacked" style={{ flex: "0 0 30%" }}>
            <label className="field-label">Title:</label>
            <div className="field-input">
              <input
                className={`form-control`}
                type="text"
                placeholder="Mr./Ms., The Rev., ..."
                value={additionalContact?.contactTitle || ""}
                disabled={true}
              />
            </div>
          </div>
          <div className="field-stacked" style={{ flex: "0 0 30%" }}>
            <label className="field-label">First Name:</label>
            <div className="field-input">
              <input
                className={`form-control`}
                type="text"
                value={additionalContact?.contactFirstName || ""}
                disabled={true}
              />
            </div>
          </div>
          <div className="field-stacked" style={{ flex: "0 0 30%" }}>
            <label className="field-label">Last Name:</label>
            <div className="field-input">
              <input
                className={`form-control`}
                type="text"
                value={additionalContact?.contactLastName || ""}
                disabled={true}
              />
            </div>
          </div>
          <div className="field-stacked" style={{ flex: "0 0 30%" }}>
            <label className="field-label">Job Title:</label>
            <div className="field-input">
              <input
                className={`form-control`}
                type="text"
                value={additionalContact?.contactJobTitle || ""}
                disabled={true}
              />
            </div>
          </div>
          <div className="field-stacked" style={{ flex: "0 0 60%" }}>
            <label className="field-label">Email:</label>
            <div className="field-input">
              <input
                className={`form-control`}
                type="email"
                value={additionalContact?.contactEmail || ""}
                disabled={true}
              />
            </div>
          </div>
        </div>
      )}

      {/* Position Codes */}
      <div className="data-container">
        <div
          className="text-header"
          style={{ flex: "0 0 20%", textWrap: "nowrap" }}
        >
          Position Codes
        </div>
        <div style={{ flex: "0 0 60%" }}>
          <button
            className="oxbtn btn-purple"
            onClick={handleNavigateToPositionCodes}
          >
            Edit
          </button>
        </div>
        {positionCodes && positionCodes.length > 0 ? (
          positionCodes.map((positionCode, index) => {
            return (
              <div
                key={positionCode.positionCodeID}
                className="position-codes-group"
              >
                <div className="text-sub-header" style={{ flex: "0 0 100%" }}>
                  Position Code {index + 1}
                </div>

                {/* Title */}
                <div className="field-long" style={{ flex: "0 0 60%" }}>
                  <label className="field-label">Title:</label>
                  <input
                    className="form-control"
                    type="text"
                    value={positionCode.positionCodeTitle || ""}
                    disabled
                  />
                </div>

                {/* FCRA Compliance */}
                {/* <div className="field-long" style={{ flex: "0 0 100%" }}>
                  <label className="field-label">FCRA Compliance:</label>
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={positionCode.fcraCompliance || false}
                    disabled
                  />
                </div> */}

                {/* Services */}
                <div
                  className="services-container"
                  style={{ flex: "0 0 100%" }}
                >
                  <div className="text-sub-sub-header">
                    Services to be Performed:
                  </div>
                  <ul>{renderServices(positionCode.positionCodeID)}</ul>
                </div>
              </div>
            );
          })
        ) : (
          <p>No position codes available for this client.</p>
        )}
      </div>

      <div className="text-body" style={{ textAlign: "center" }}>
        After clicking 'Save and Complete', your information will be saved, and
        you will be redirected to the login screen. A PDF of your saved
        information will also be downloaded for your records, allowing you to
        print a copy if needed. Please log in using your email to access your
        account moving forward.
      </div>
      <br></br>
      <div className="btn-center">
        <button className="oxbtn btn-green" onClick={() => handleSave()}>
          Save and Complete
        </button>
      </div>
      {loading && (
        <div className="loading-mask">
          <div className="loading-spinner"></div>
        </div>
      )}
      {successMessage && (
        <div className="alert-message alert alert-success alert-dismissible fade show">
          {successMessage}
          <button
            type="button"
            className="btn-close"
            onClick={() => setSuccessMessage("")}
            aria-label="Close"
          ></button>
        </div>
      )}
      {errorMessage && (
        <div
          className="alert alert-danger alert-dismissible fade show"
          role="alert"
        >
          {errorMessage}
          <button
            type="button"
            className="btn-close"
            onClick={() => setErrorMessage("")}
            aria-label="Close"
          ></button>
        </div>
      )}
    </div>
  );
};

export default Confirmation;
